import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, BookOpen, CalendarClock, ChevronRight, Flame, GraduationCap, HandHeart, HeartHandshake, History, MapPin, Sparkles, Users, CheckCircle2, Sunrise, Sun, Sunset, Moon, Quote, HandCoins } from "lucide-react";
import { LevelBadge, NotificationBell } from "@/pages/portal/Widgets";
import { useAuth } from "@/context/AuthContext";
import { fileUrl, nextOccurrence, fmtDate, rupiah } from "@/lib/api";
import { card, useApi, Stagger, Item, Skeleton } from "@/components/portal/ui";
import DigitalCard from "@/pages/portal/DigitalCard";
import BirthdayCard from "@/pages/portal/BirthdayCard";
import { CoverImage } from "@/components/CoverImage";

const pad = (v) => String(v).padStart(2, "0");

function greeting() {
  // WITA (Asia/Makassar) = UTC+8
  const h = (new Date().getUTCHours() + 8) % 24;
  if (h >= 5 && h < 11) return { text: "Selamat pagi", Icon: Sunrise, color: "text-amber-200" };
  if (h >= 11 && h < 15) return { text: "Selamat siang", Icon: Sun, color: "text-yellow-200" };
  if (h >= 15 && h < 18) return { text: "Selamat sore", Icon: Sunset, color: "text-orange-200" };
  return { text: "Selamat malam", Icon: Moon, color: "text-indigo-200" };
}

const DAILY_VERSES = [
  ["Segala perkara dapat kutanggung di dalam Dia yang memberi kekuatan kepadaku.", "Filipi 4:13"],
  ["Serahkanlah segala kekuatiranmu kepada-Nya, sebab Ia yang memelihara kamu.", "1 Petrus 5:7"],
  ["TUHAN adalah gembalaku, takkan kekurangan aku.", "Mazmur 23:1"],
  ["Kuatkanlah dan teguhkanlah hatimu, janganlah takut.", "Yosua 1:9"],
  ["Sebab Aku ini mengetahui rancangan-rancangan apa yang ada pada-Ku mengenai kamu.", "Yeremia 29:11"],
  ["Mintalah, maka akan diberikan kepadamu; carilah, maka kamu akan mendapat.", "Matius 7:7"],
  ["Percayalah kepada TUHAN dengan segenap hatimu.", "Amsal 3:5"],
  ["Kasih karunia cukup bagimu, sebab justru dalam kelemahanlah kuasa-Ku menjadi sempurna.", "2 Korintus 12:9"],
  ["Berbahagialah orang yang lapar dan haus akan kebenaran, karena mereka akan dipuaskan.", "Matius 5:6"],
  ["Nyanyikanlah nyanyian baru bagi TUHAN, sebab Ia telah melakukan perbuatan-perbuatan yang ajaib.", "Mazmur 98:1"],
  ["Hendaklah kamu selalu bersukacita dalam Tuhan!", "Filipi 4:4"],
  ["Berjaga-jagalah dan berdoalah, supaya kamu jangan jatuh ke dalam pencobaan.", "Matius 26:41"],
  ["Sebab di mana dua atau tiga orang berkumpul dalam Nama-Ku, di situ Aku ada.", "Matius 18:20"],
  ["Tetapi carilah dahulu Kerajaan Allah dan kebenarannya.", "Matius 6:33"],
  ["Besarlah kesetiaan-Mu! Tak berkesudahan kasih setia TUHAN.", "Ratapan 3:22-23"],
];

function dailyVerse() {
  const now = new Date();
  const start = Date.UTC(now.getUTCFullYear(), 0, 0);
  const day = Math.floor((Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) - start) / 86400000);
  return DAILY_VERSES[day % DAILY_VERSES.length];
}

export function SpecialBanner({ events = [], linkBase = "/portal/events" }) {
  if (!events.length) return null;
  const e = events[0];
  return (
    <Link to={linkBase} data-testid="special-event-banner" className="relative block overflow-hidden rounded-3xl bg-slate-900 text-white p-5 shadow-lg">
      {e.image_url && <img src={fileUrl(e.image_url)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />}
      <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/60 to-transparent" />
      <div className="relative">
        <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.2em] bg-amber-400 text-slate-900 rounded-full px-3 py-1"><Sparkles className="w-3 h-3" />Event Khusus</span>
        <p className="mt-3 text-xl font-black tracking-tight">{e.title}</p>
        <p className="mt-1 text-xs text-white/70">{e.banner_text || `${fmtDate(e.start_date)} · ${e.location}`}</p>
        <p className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-amber-300">{(e.price || 0) > 0 ? `${rupiah(e.price)} · Daftar sekarang` : "Gratis · Daftar sekarang"} <ChevronRight className="w-3.5 h-3.5" /></p>
      </div>
    </Link>
  );
}

function Countdown({ schedules }) {
  const next = useMemo(() => {
    const list = (schedules || []).map((s) => ({ s, t: nextOccurrence(s) })).sort((a, b) => a.t - b.t);
    return list[0];
  }, [schedules]);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!next) return null;
  let s = Math.max(0, Math.floor((next.t - now) / 1000));
  const parts = [["Hari", Math.floor(s / 86400)], ["Jam", Math.floor((s %= 86400) / 3600)], ["Menit", Math.floor((s % 3600) / 60)], ["Detik", s % 60]];
  return (
    <div className={card} data-testid="home-countdown-card">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-bold bg-blue-100 text-blue-700 rounded-full px-3 py-1">{next.s.category}</span>
        <span className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{next.s.location}</span>
      </div>
      <p className="mt-4 text-slate-900 font-extrabold text-lg">{next.s.title} · {next.s.time} WITA</p>
      <div className="mt-4 grid grid-cols-4 gap-2 text-center" data-testid="home-countdown">
        {parts.map(([l, v], i) => (
          <div key={l} className={`${i === 3 ? "bg-blue-600" : "bg-slate-50"} rounded-2xl py-3`}>
            <p className={`text-xl font-black tabular-nums ${i === 3 ? "text-white" : "text-slate-900"}`}>{pad(v)}</p>
            <p className={`text-[10px] font-semibold uppercase ${i === 3 ? "text-blue-100" : "text-slate-500"}`}>{l}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

const ACTIONS = [
  ["/portal/renungan", "Renungan", BookOpen, "text-indigo-600", "quick-renungan"],
  ["/portal/persembahan", "Persembahan", HandCoins, "text-blue-600", "quick-persembahan"],
  ["/portal/ministry", "Pelayanan", Users, "text-emerald-500", "quick-ministry"],
  ["/portal/events", "Events", CalendarClock, "text-amber-700", "quick-events"],
  ["/portal/doa", "Doa", HeartHandshake, "text-red-600", "quick-doa"],
  ["/portal/kehadiran", "Kehadiran", History, "text-slate-700", "quick-kehadiran"],
  ["/portal/elearning", "E-Learning", GraduationCap, "text-violet-600", "quick-elearning"],
  ["/portal/serve-up", "Serve-Up", HandHeart, "text-rose-600", "quick-serve-up"],
];

export default function PortalHome() {
  const { user } = useAuth();
  const { data } = useApi("/portal/home");
  const first = user.name.split(" ")[0];
  const g = greeting();
  const [verse, verseRef] = dailyVerse();

  return (
    <div data-testid="portal-home">
      <header className="relative rounded-b-[2.5rem] bg-blue-600 text-white p-6 pb-20">
        <div className="absolute inset-0 overflow-hidden rounded-b-[2.5rem] pointer-events-none">
          <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-white/10" />
          <div className="absolute top-24 -left-10 w-32 h-32 rounded-full bg-indigo-600/40" />
        </div>
        <div className="relative flex items-start justify-between pt-6">
          <div>
            <p data-testid="portal-greeting" className="text-sm text-blue-100 font-medium flex items-center gap-1.5"><g.Icon className={`w-4 h-4 ${g.color}`} /> {g.text},</p>
            <h1 data-testid="portal-member-name" className="text-3xl font-extrabold tracking-tight mt-1">{first}</h1>
            <span data-testid="portal-nig-badge" className="inline-flex items-center gap-1.5 mt-3 bg-white/15 border border-white/25 backdrop-blur-md rounded-full px-3 py-1 text-[11px] font-bold tracking-wide"><BadgeCheck className="w-3.5 h-3.5" /> NIG {user.nig}</span>
            <div data-testid="portal-daily-verse" className="mt-4 flex items-start gap-2 max-w-[15rem]">
              <Quote className="w-4 h-4 text-amber-200 shrink-0 mt-0.5" />
              <p className="text-xs text-blue-50 leading-snug italic">“{verse}” <span className="not-italic font-bold text-amber-200">— {verseRef}</span></p>
            </div>
          </div>
          <div className="flex items-center gap-2">
          <NotificationBell items={data?.notifications} />
          <Link to="/portal/profile" data-testid="home-avatar-link">
            {user.picture ? <img src={fileUrl(user.picture)} alt={user.name} className="w-12 h-12 rounded-full object-cover ring-2 ring-white/50" /> : <span className="w-12 h-12 rounded-full bg-white/20 ring-2 ring-white/40 grid place-items-center font-black text-lg">{first[0]}</span>}
          </Link>
          </div>
        </div>
      </header>

      <DigitalCard user={user} komsel={data?.komsel} />

      <Stagger className="px-5 mt-7 grid grid-cols-3 gap-3">
        {ACTIONS.map(([to, label, Icon, color, id]) => (
          <Item key={to}>
            <Link to={to} data-testid={id} className="group flex flex-col items-center gap-2 active:scale-95 transition">
              <span className={`w-full h-16 rounded-2xl bg-white border border-slate-100 shadow-sm grid place-items-center ${color} group-hover:-translate-y-1 group-hover:shadow-md transition duration-300`}><Icon className="w-6 h-6" /></span>
              <span className="text-[11px] font-semibold text-slate-600">{label}</span>
            </Link>
          </Item>
        ))}
      </Stagger>

      <div className="px-5 mt-7 space-y-5">
        {!data ? <Skeleton className="h-44" /> : (
          <>
            <SpecialBanner events={data.special_events || []} />
            {(data.worship_posts || []).length > 0 && (
              <div data-testid="worship-posts">
                <p className="text-sm font-bold text-slate-900 px-1">Ibadah & Pengumuman</p>
                <div className="mt-3 flex gap-3 overflow-x-auto hide-scroll -mx-5 px-5 pb-1">
                  {data.worship_posts.map((e) => (
                    <Link key={e.id} to="/portal/events" data-testid={`worship-post-${e.id}`} className="shrink-0 w-60 rounded-3xl overflow-hidden bg-white border border-slate-100 shadow-sm active:scale-[0.98] transition">
                      {(e.image_url_portal || e.image_url) && <CoverImage src={fileUrl(e.image_url_portal || e.image_url)} alt={e.title} className="w-full h-36" />}
                      <div className="p-4">
                        <p className="text-[10px] font-black uppercase tracking-wider text-blue-600">Tanpa registrasi</p>
                        <p className="mt-1 font-bold text-slate-900 leading-snug line-clamp-2">{e.title}</p>
                        <p className="mt-1 text-xs text-slate-500">{fmtDate(e.start_date)}{e.time ? ` · ${e.time} WITA` : ""}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
            <BirthdayCard user={user} birthday={data.birthday} />
            <LevelBadge level={data.level} />
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white rounded-3xl p-5 border border-slate-100 shadow-sm" data-testid="home-streak-stat"><Flame className="w-5 h-5 text-amber-700" /><p className="mt-3 text-2xl font-black text-slate-900">{data.streak.streak} hari</p><p className="text-xs text-slate-500">Streak renungan</p></div>
              <div className="bg-white rounded-3xl p-5 border border-slate-100 shadow-sm" data-testid="home-attendance-stat"><CheckCircle2 className="w-5 h-5 text-emerald-500" /><p className="mt-3 text-2xl font-black text-slate-900">{data.attendance}x</p><p className="text-xs text-slate-500">Kehadiran ibadah</p></div>
            </div>
            <Countdown schedules={data.schedules} />
            {data.devotion && (
              <Link to="/portal/renungan" data-testid="home-renungan-card" className="w-full text-left bg-white rounded-3xl p-3 shadow-sm border border-slate-100 hover:-translate-y-1 transition duration-300 flex gap-4 items-center">
                <img src={fileUrl(data.devotion.image_url)} alt="" className="w-24 h-24 rounded-2xl object-cover" />
                <div className="flex-1 pr-2">
                  <p className="text-[11px] font-bold text-blue-600 uppercase tracking-wider">Renungan Hari Ini</p>
                  <p className="font-bold text-slate-900 mt-1 leading-snug">{data.devotion.title}</p>
                  <p className="text-xs text-slate-500 mt-1">{data.devotion.verse}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400 mr-2" />
              </Link>
            )}
          </>
        )}
      </div>
    </div>
  );
}
